import type { RegionGroup } from "app/pages/sales-intelligence/utils/location";
import type { IRegionObject } from "app/services/RegionService";

import type { VisibleRegionGroup } from "./types";

const filterCountriesByTerm = <TCountry extends { text: string }>(
    list: readonly TCountry[],
    term: string,
): TCountry[] => {
    const t = term.trim().toLowerCase();
    if (!t) {
        return [...list];
    }
    return list.filter((c) => c.text.toLowerCase().includes(t));
};

const regionLabelMatches = (region: IRegionObject, term: string): boolean => {
    const t = term.trim().toLowerCase();
    if (!t) {
        return true;
    }
    const label = (region.text ?? region.name).toLowerCase();
    return label.includes(t);
};

export type VisibleNestedGroup<
    TCountry extends { id: number; text: string },
    TGroup extends {
        region: IRegionObject;
        countries: readonly TCountry[];
        subGroups: readonly TGroup[];
    },
> = {
    group: TGroup;
    filteredCountries: readonly TCountry[];
    filteredSubGroups: readonly VisibleNestedGroup<TCountry, TGroup>[];
};

const keepWholeGroup = <
    TCountry extends { id: number; text: string },
    TGroup extends {
        region: IRegionObject;
        countries: readonly TCountry[];
        subGroups: readonly TGroup[];
    },
>(
    group: TGroup,
): VisibleNestedGroup<TCountry, TGroup> => ({
    group,
    filteredCountries: [...group.countries],
    filteredSubGroups: group.subGroups.map((subGroup) => keepWholeGroup<TCountry, TGroup>(subGroup)),
});

const filterVisibleNestedGroup = <
    TCountry extends { id: number; text: string },
    TGroup extends {
        region: IRegionObject;
        countries: readonly TCountry[];
        subGroups: readonly TGroup[];
    },
>(
    group: TGroup,
    searchTerm: string,
): VisibleNestedGroup<TCountry, TGroup> | null => {
    const term = searchTerm.toLowerCase().trim();

    // A region matched by its own label stays whole: filtering its countries by the
    // region term would leave it empty and impossible to pick from.
    if (!term || regionLabelMatches(group.region, searchTerm)) {
        return keepWholeGroup<TCountry, TGroup>(group);
    }

    const filteredCountries = filterCountriesByTerm(group.countries, searchTerm);
    const filteredSubGroups = group.subGroups
        .map((subGroup) => filterVisibleNestedGroup<TCountry, TGroup>(subGroup, searchTerm))
        .filter((visible): visible is VisibleNestedGroup<TCountry, TGroup> => visible !== null);

    if (filteredCountries.length === 0 && filteredSubGroups.length === 0) {
        return null;
    }

    return {
        group,
        filteredCountries,
        filteredSubGroups,
    };
};

export const getVisibleNestedGroups = <
    TCountry extends { id: number; text: string },
    TGroup extends {
        region: IRegionObject;
        countries: readonly TCountry[];
        subGroups: readonly TGroup[];
    },
>(
    groups: readonly TGroup[],
    searchTerm: string,
): VisibleNestedGroup<TCountry, TGroup>[] =>
    groups
        .map((group) => filterVisibleNestedGroup<TCountry, TGroup>(group, searchTerm))
        .filter((visible): visible is VisibleNestedGroup<TCountry, TGroup> => visible !== null);

export const getVisibleRegionGroups = (groups: readonly RegionGroup[], searchTerm: string): VisibleRegionGroup[] =>
    getVisibleNestedGroups<RegionGroup["memberCountries"][number], RegionGroup>(groups, searchTerm);

const collectVisibleNestedCountryIds = (visible: VisibleRegionGroup, ids: Set<number>): void => {
    for (const country of visible.filteredCountries) {
        ids.add(country.id);
    }

    for (const subGroup of visible.filteredSubGroups) {
        collectVisibleNestedCountryIds(subGroup, ids);
    }
};

export const countVisibleNestedCountries = (visible: VisibleRegionGroup): number => {
    const ids = new Set<number>();
    collectVisibleNestedCountryIds(visible, ids);
    return ids.size;
};

export const isRegionExpandedWhenSearching = (
    manualExpanded: ReadonlySet<string>,
    searchActive: boolean,
    regionName: string,
): boolean => manualExpanded.has(regionName) || searchActive;
